import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useDispatch } from 'react-redux'

import {
  getCityAndStateFromZipCode,
  GoogleMapsContext,
} from 'utils/apis/googleGeocoding'

import {
  InsuranceType,
  InsuranceTypeState,
} from 'state/insuranceTypes/types'

import { setCurrentQuoteRequestEntry } from 'state/quotes/actions'

import Content from 'components/base/Content'
import Title from 'components/base/Title'
import ButtonRow from 'components/base/Button/Row'
import Button from 'components/base/Button'
import Form from 'components/base/Form'
import InputGroup from 'components/base/Form/InputGroup'
import UserSuppliedText from 'components/base/UserSuppliedText'
import { Type as InputType } from 'components/base/Form/Input/types'

import QuotingWrapper, { Theme as QuotingWrapperTheme } from 'components/blocks/QuotingWrapper'

import {
  events,
  useGoogleTagManager,
} from 'hooks/useGoogleTagManager'

import { Props } from './types'

const QuotingZipCode: React.FC<Props> = ({
  applicantFirstName,
  applicantZipCode,
  insuranceTypes,
  zipCodeRules,
}): React.ReactElement => {
  const { isLoaded: isGoogleMapsLoaded } = useContext(GoogleMapsContext)

  const dispatch = useDispatch()

  const { pushDataLayerEvent } = useGoogleTagManager()

  const [
    isValid,
    setIsValid,
  ] = useState(false)

  const [
    isLoading,
    setIsLoading,
  ] = useState(false)

  useEffect(() => {
    if (!isGoogleMapsLoaded) {
      return
    }

    const updateCityAndStateAndAvailableInsuranceTypes = async (): Promise<false | undefined> => {
      setIsValid(false)

      const applicantZipCodeString: string = applicantZipCode || ''
      if (
        !insuranceTypes ||
        !applicantZipCodeString ||
        !applicantZipCodeString.match(zipCodeRules)
      ) {
        return false
      }

      setIsLoading(true)
      setIsValid(true)

      const cityAndState = await getCityAndStateFromZipCode(applicantZipCodeString)

      if (!cityAndState) {
        setIsValid(false)

        return false
      }

      const {
        city = undefined,
        state: { abbr: stateAbbr = undefined } = {},
      } = cityAndState

      dispatch(setCurrentQuoteRequestEntry('applicant.address.city', city))
      dispatch(setCurrentQuoteRequestEntry('applicant.address.stateAbbr', stateAbbr))

      const availableInsuranceTypes =
        (stateAbbr &&
          insuranceTypes &&
          insuranceTypes
            .filter((insuranceType: InsuranceType) =>
              insuranceType.states.find(
                (state: InsuranceTypeState) => state.abbr.toLowerCase() === stateAbbr.toLowerCase(),
              ),
            )
            .map((insuranceType: InsuranceType) => ({
              ...insuranceType,
              isAvailableOnline: (insuranceType.states.find((state: InsuranceTypeState) => state.abbr.toLowerCase() === stateAbbr.toLowerCase()) || {}).is_available_online,
            }))
        ) || undefined

      dispatch(setCurrentQuoteRequestEntry('availableInsuranceTypes', availableInsuranceTypes))

      setIsLoading(false)

      return undefined
    }
    updateCityAndStateAndAvailableInsuranceTypes()
  }, [
    applicantZipCode,
    dispatch,
    insuranceTypes,
    isGoogleMapsLoaded,
    zipCodeRules,
  ])

  const validateZipCode = useCallback(async (value: string): Promise<boolean> => {
    if (!isGoogleMapsLoaded) {
      return true
    }

    const cityAndState = await getCityAndStateFromZipCode(value)

    return (cityAndState && cityAndState.isValid) || false
  }, [ isGoogleMapsLoaded ])

  return (
    <QuotingWrapper noPadding={true} theme={QuotingWrapperTheme.concierge} title='Great!'>
      <Title level={3} noMargin={true}>
        Thanks<UserSuppliedText>{(applicantFirstName && `, ${applicantFirstName}`) || undefined}</UserSuppliedText>.
        <br />
        What is your zip code?
      </Title>
      <Content margined={true}>
        <Form
          short={true}
          nextUrl='quote/new/insurance-types'
          onSubmit={() => pushDataLayerEvent(events.zip_submitted)}
        >
          <InputGroup
            type={InputType.text}
            label='Zip Code'
            name='zipCode'
            quoteRequestField='applicant.address.zipCode'
            rules={`required|string|match:pattern=${zipCodeRules}`}
            asyncValidate={async (value: string): Promise<boolean> => await validateZipCode(value)}
            errorText='A valid zip code is required.'
            placeholder='e.g. 97214'
            maxLength='10'
          />
          <ButtonRow>
            <Button center={true} wide={true} isQuoteRequestNext={true} disabled={isLoading || !isValid}>
              Next
            </Button>
          </ButtonRow>
        </Form>
      </Content>
    </QuotingWrapper>
  )
}

export default QuotingZipCode
